import { createFileRoute, redirect, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api } from "../../lib/api";
import type { CurrentUser } from "../../lib/auth";
import { DialogPanel } from "../../components/shared/DialogPanel";
import { XIcon } from "../../components/shared/Icons";
import { backdropClose } from "../../components/shared/backdrop";

// Self-Service: contas do portal do cliente (agentes.revendai.com). Só Admin.
// Cada conta = login/senha + Agente Global que rege + teto de agentes.
// API: apps/api/.../routers/ss_admin.py.

export const Route = createFileRoute("/_authed/self-service")({
  beforeLoad: ({ context }) => {
    const u = (context as { user?: CurrentUser }).user;
    if (u && u.role_name !== "Admin") throw redirect({ to: "/" });
  },
  component: SelfServicePage,
});

interface Account {
  id: string; name: string; login: string; global_agent_id: string; global_agent_name: string | null;
  max_agents: number; disabled: boolean; last_login_at: string | null;
  agents: { id: string; name: string; status: string }[];
}
interface GlobalAgent { id: string; name: string }

const btnPrimary: React.CSSProperties = { background: "var(--brand)", color: "#fff", padding: "7px 14px", borderRadius: 8, fontSize: 13, fontWeight: 500, border: "none", cursor: "pointer" };
const btnGhost: React.CSSProperties = { background: "none", color: "var(--ink-secondary)", padding: "7px 14px", borderRadius: 8, fontSize: 13, border: "1px solid var(--line-strong)", cursor: "pointer" };
const inputStyle: React.CSSProperties = { padding: "8px 12px", background: "var(--surface-input)", border: "1px solid var(--line)", borderRadius: 8, color: "var(--ink)", fontSize: 13, outline: "none", width: "100%", boxSizing: "border-box" };
const th: React.CSSProperties = { textAlign: "left", fontSize: 11, color: "var(--ink-dim)", textTransform: "uppercase", letterSpacing: ".04em", padding: "10px 12px", borderBottom: "1px solid var(--line)" };
const td: React.CSSProperties = { padding: "10px 12px", fontSize: 13, borderBottom: "1px solid var(--line-soft)", verticalAlign: "top" };

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div {...backdropClose(onClose)} style={{ position: "fixed", inset: 0, background: "var(--overlay)", zIndex: "var(--z-modal)" as any, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <DialogPanel onClose={onClose} label={title} style={{ background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 12, width: 460, padding: 24, display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontSize: 15, fontWeight: 600 }}>{title}</span>
          <button aria-label="Fechar" onClick={onClose} style={{ color: "var(--ink-muted)", background: "none", border: "none", cursor: "pointer" }}><XIcon size={14} /></button>
        </div>
        {children}
      </DialogPanel>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div style={{ display: "flex", flexDirection: "column", gap: 6 }}><label style={{ fontSize: 12, color: "var(--ink-secondary)" }}>{label}</label>{children}</div>;
}

type Form = { name: string; login: string; password: string; global_agent_id: string; max_agents: number; disabled: boolean };

function AccountModal({ initial, globals, onClose }: { initial: Account | null; globals: GlobalAgent[]; onClose: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState<Form>({
    name: initial?.name ?? "", login: initial?.login ?? "", password: "",
    global_agent_id: initial?.global_agent_id ?? globals[0]?.id ?? "", max_agents: initial?.max_agents ?? 1, disabled: initial?.disabled ?? false,
  });
  const set = (p: Partial<Form>) => setForm((f) => ({ ...f, ...p }));
  const mut = useMutation({
    mutationFn: () => initial
      ? api.patch(`/ss-admin/${initial.id}`, { name: form.name, password: form.password || undefined, global_agent_id: form.global_agent_id, max_agents: form.max_agents, disabled: form.disabled })
      : api.post("/ss-admin", { name: form.name, login: form.login, password: form.password, global_agent_id: form.global_agent_id, max_agents: form.max_agents }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["ss-accounts"] }); onClose(); },
  });
  const err = mut.error as { payload?: { detail?: string } } | null;
  return (
    <Modal title={initial ? "Editar conta" : "Nova conta"} onClose={onClose}>
      <Field label="Nome (empresa)"><input style={inputStyle} value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder="Auto Center Sul" /></Field>
      <Field label="Login">
        <input style={{ ...inputStyle, fontFamily: "var(--font-mono)" }} value={form.login} disabled={!!initial} onChange={(e) => set({ login: e.target.value.toLowerCase() })} placeholder="autocentersul" />
      </Field>
      <Field label={initial ? "Nova senha (vazio = mantém; trocar derruba as sessões)" : "Senha (mín. 8)"}>
        <input style={inputStyle} type="text" value={form.password} onChange={(e) => set({ password: e.target.value })} autoComplete="off" />
      </Field>
      <Field label="Agente Global que rege a conta">
        <select style={inputStyle} value={form.global_agent_id} onChange={(e) => set({ global_agent_id: e.target.value })}>
          {globals.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
        </select>
        {initial && <span style={{ fontSize: 11, color: "var(--ink-dim)" }}>Trocar só vale pra agentes novos.</span>}
      </Field>
      <Field label="Máximo de agentes"><input style={inputStyle} type="number" min={1} max={100} value={form.max_agents} onChange={(e) => set({ max_agents: Number(e.target.value) || 1 })} /></Field>
      {initial && (
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
          <input type="checkbox" checked={form.disabled} onChange={(e) => set({ disabled: e.target.checked })} style={{ accentColor: "var(--brand)" }} /> Conta desativada (bloqueia o login)
        </label>
      )}
      {err && <div style={{ fontSize: 12, color: "var(--danger)" }}>{err.payload?.detail ?? "Erro ao salvar"}</div>}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button style={btnGhost} onClick={onClose}>Cancelar</button>
        <button style={btnPrimary} disabled={mut.isPending || !form.name || !form.global_agent_id || (!initial && (!form.login || form.password.length < 8))} onClick={() => mut.mutate()}>
          {mut.isPending ? "Salvando…" : initial ? "Salvar" : "Criar conta"}
        </button>
      </div>
    </Modal>
  );
}

function SelfServicePage() {
  const qc = useQueryClient();
  const { data: accounts = [], isLoading } = useQuery<Account[]>({ queryKey: ["ss-accounts"], queryFn: () => api.get("/ss-admin") });
  const { data: globals = [] } = useQuery<GlobalAgent[]>({ queryKey: ["agents-globals"], queryFn: () => api.get("/agents/globals") });
  const [editing, setEditing] = useState<Account | null | "new">(null);
  const del = useMutation({
    mutationFn: (id: string) => api.delete(`/ss-admin/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ss-accounts"] }),
  });

  return (
    <div style={{ padding: "28px 32px", maxWidth: 1120, margin: "0 auto", display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontFamily: "var(--font-display)", fontSize: 20, fontWeight: 600 }}>Self-service</h1>
          <p style={{ margin: "4px 0 0", color: "var(--ink-muted)", fontSize: 13 }}>Contas do portal do cliente (agentes.revendai.com). O cliente entra com login e senha e configura os próprios agentes a partir do Agente Global da conta.</p>
        </div>
        <button style={btnPrimary} onClick={() => setEditing("new")} disabled={!globals.length} title={globals.length ? "" : "Publique um Agente Global primeiro"}>+ Nova conta</button>
      </div>

      <div style={{ background: "var(--surface-card)", border: "1px solid var(--line)", borderRadius: 10, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr><th style={th}>Conta</th><th style={th}>Login</th><th style={th}>Agente Global</th><th style={th}>Agentes</th><th style={th}>Último acesso</th><th style={th} /></tr></thead>
          <tbody>
            {isLoading && <tr><td style={td} colSpan={6}>Carregando…</td></tr>}
            {!isLoading && !accounts.length && <tr><td style={{ ...td, color: "var(--ink-muted)", textAlign: "center", padding: 40 }} colSpan={6}>Nenhuma conta ainda.</td></tr>}
            {accounts.map((a) => (
              <tr key={a.id} style={{ opacity: a.disabled ? 0.55 : 1 }}>
                <td style={td}><div style={{ fontWeight: 500 }}>{a.name}</div>{a.disabled && <span style={{ fontSize: 11, color: "var(--danger)" }}>desativada</span>}</td>
                <td style={{ ...td, fontFamily: "var(--font-mono)" }}>{a.login}</td>
                <td style={td}>{a.global_agent_name ?? <span style={{ color: "var(--ink-muted)" }}>—</span>}</td>
                <td style={td}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {a.agents.map((ag) => (
                      <Link key={ag.id} to="/agents/$agentId" params={{ agentId: ag.id }} style={{ color: "var(--brand)", fontSize: 13 }}>{ag.name}</Link>
                    ))}
                    <span style={{ fontSize: 11, color: "var(--ink-dim)" }}>{a.agents.length} / {a.max_agents}</span>
                  </div>
                </td>
                <td style={{ ...td, color: "var(--ink-muted)" }}>{a.last_login_at ? new Date(a.last_login_at).toLocaleString("pt-BR") : "nunca"}</td>
                <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}>
                  <button style={{ ...btnGhost, padding: "5px 10px", fontSize: 12 }} onClick={() => setEditing(a)}>Editar</button>{" "}
                  <button style={{ ...btnGhost, padding: "5px 10px", fontSize: 12, color: "var(--danger)" }} disabled={!!a.agents.length} title={a.agents.length ? "Exclua os agentes da conta antes" : ""}
                    onClick={() => { if (confirm(`Excluir a conta ${a.name}?`)) del.mutate(a.id); }}>Excluir</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && <AccountModal initial={editing === "new" ? null : editing} globals={globals} onClose={() => setEditing(null)} />}
    </div>
  );
}
